import React, { useCallback } from 'react';
import { useRouter } from 'next/router';
import * as S from './DesktopFilters.styles';
import { DesktopFilterSelectOptionProps } from './DesktopFilterSelectOption.types';
import {
  getDesktopFilterSelectOptionHref,
  preserveUserIdInDesktopFilterQuery,
} from './DesktopFilterSelectOption.helpers';
import { QUERY } from '@/constants/common';
import { KeywordsSource } from '@/components/SearchPanel/components/SearchSuggestions.types';
import {
  addQueryParamToPath,
  cleanupSeoUrl,
  isModifiedClick,
} from '@/utils/helpers';

export const DesktopFilterSelectOption = ({
  dataTestId,
  label,
  hrefParams,
  isSelected,
  onClick,
  onNavigateHref,
}: DesktopFilterSelectOptionProps) => {
  const router = useRouter();
  const rawHref = getDesktopFilterSelectOptionHref(hrefParams);
  // Crawlable markup gets a cleaned, canonically-ordered href (no
  // keywords_source, button, mobile_site, utm_*, …). The real click below
  // navigates via rawHref and re-adds keywords_source, so runtime navigation
  // is unchanged — only what Googlebot reads from the DOM is sanitized.
  const href = cleanupSeoUrl(rawHref);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      onClick?.();

      // Let the browser open a new tab/window natively (Cmd/Ctrl/Shift/Alt-click)
      if (isModifiedClick(e)) return;

      const navigationHref = addQueryParamToPath(
        rawHref,
        QUERY.keywordsSource,
        KeywordsSource.Typewritten
      );

      if (onNavigateHref) {
        e.preventDefault();
        onNavigateHref(navigationHref);
        return;
      }

      e.preventDefault();
      router.push(
        navigationHref,
        undefined,
        preserveUserIdInDesktopFilterQuery(router.asPath)
          ? { scroll: false, shallow: true }
          : undefined
      );
    },
    [rawHref, onClick, onNavigateHref, router]
  );

  return (
    <S.LinkOption
      key={label}
      dataTestId={dataTestId}
      href={href}
      $isActive={isSelected}
      onClick={handleClick}
    >
      <S.RadioButton $isActive={isSelected} />
      <S.LinkOptionText>{label}</S.LinkOptionText>
    </S.LinkOption>
  );
};
